import React, { useEffect, useState } from 'react';
import { useReducedMotion } from 'motion/react';
import NovaMark from './NovaMark';
import { novaThemes, NovaThemeName } from './novaTheme';

const baseSteps = (published12mFilter?: boolean) => [
  'Searching Stylus intelligence…',
  'Scanning relevant reports',
  published12mFilter ? 'Selecting the most relevant sources, last 12 months' : 'Selecting the most relevant sources',
  'Synthesising the answer…',
];

interface NovaThinkingIndicatorProps {
  theme: NovaThemeName;
  /** Whether the "last 12 months" filter is active for this query — reflected in the step copy. */
  published12mFilter?: boolean;
  markSize?: number;
}

// A real streaming backend would push these steps live; until then this is a
// simulated reveal so the "thinking" state matches the spec's visual intent
// while the underlying novaChat() call is a single buffered mock response.
const NovaThinkingIndicator: React.FC<NovaThinkingIndicatorProps> = ({ theme, published12mFilter, markSize = 100 }) => {
  const [stepIndex, setStepIndex] = useState(0);
  const surfaces = novaThemes[theme];
  const STEPS = baseSteps(published12mFilter);
  const prefersReducedMotion = useReducedMotion();

  useEffect(() => {
    if (stepIndex >= STEPS.length - 1) return;
    const timeout = setTimeout(() => setStepIndex((i) => i + 1), 700);
    return () => clearTimeout(timeout);
  }, [stepIndex]);

  return (
    <div className="flex flex-col items-start">
      <div
        className="w-fit max-w-[85%] space-y-1.5 rounded-2xl p-3 text-sm"
        style={{ background: surfaces.surfaces.novaBubble }}
      >
        {STEPS.slice(0, stepIndex + 1).map((step, index) => {
          const isCurrent = index === stepIndex;
          return (
            <div key={step} className="flex items-center gap-2" style={{ color: surfaces.ink.secondary }}>
              {isCurrent ? (
                <span
                  className={`h-3.5 w-3.5 shrink-0 rounded-full border-2 border-transparent ${prefersReducedMotion ? '' : 'animate-spin'}`}
                  style={{ borderTopColor: surfaces.actionAccent, borderRightColor: surfaces.actionAccent }}
                />
              ) : (
                <span className="shrink-0" style={{ color: surfaces.ink.heading }}>
                  ✓
                </span>
              )}
              {step}
            </div>
          );
        })}
      </div>
      {/* Spec note 02: matches every response indicator in the buildout, left-aligned
          to the text column, not in a gutter beside it. */}
      <NovaMark state="investigating" theme={theme} size={markSize} className="mt-6" />
    </div>
  );
};

export default NovaThinkingIndicator;
